import React, { useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup as MUIRadioGroup,
    RadioGroupProps,
    Tooltip,
    Typography,
} from '@mui/material';
import { StepperControls, StepWrapper, StyledCardSection } from '@fnol/library/src/components';
import { checkIfSerbianPhoneNumber, checkPhoneNumber, common, currentYear } from '@fnol/library/src/lib';
import { legalPersonType, damageType, personType, translations, requestType } from '../../lib/consts';
import { checkVehicleRegistration, checkVIN } from '../../lib/helpers';
import { useValidateIndividual, useValidateLegalEntity } from '../../lib/customHooks';
import DamageVehicle from '../DamageVehicle';
import DamageObject from '../DamageObject';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
};

interface IFormData {
    submitterType: legalPersonType;
    damageType: string;
    ownerType: legalPersonType;
    userType: legalPersonType;
    vehicleType: string;
    chassisNumber: string;
    chassisNumberByPolicy: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleReg: string;
    vehicleProductionYear: string;
    objectOwnerSubmitter: string;
    ownerBuildingType: legalPersonType;
    objectType: string;
    objectDescription: string;
    objectConstructionYear: string;
    objectLocation: string;
    objectDamageDescription: string;
    moreObjectDamagesDescription: string;
    reportedClaims: Array<GenericObject>;
    materialDamage: boolean;
    immaterialOrBothDamage: boolean;
    isSubmitterOwnerOfVehicle: string;
    submitterUserByVehicleRegistration: string;
    ownerIndividualSubmitterRelationId: number;
    ownerLegalEntityPersonRoleId: number;
    pickDriver: number;
    vehicleDamageMobile: string;
    isVehicleDrivable: string;
    vehicleLocation: string;
    howIsVehicleUsed: string;
    isVehicleParked: string;
    smsLink: string;
    sendLink: number;
    vehicleDamageDescription: string;
    requestType: number;
    submitterLegalEntityContactPhone: string;
    mobileLabelStar: boolean;
    submittedSuccessfully: boolean;
    submitError: string;
    driver: number;
}

const DamageScreen = ({ navigator }: Props) => {
    const { values, errors, touched, setErrors, setTouched, submitForm, setFieldValue, isSubmitting } =
        useFormikContext<IFormData>();

    //owner validation
    const ownerLegalValidation = useValidateLegalEntity(personType.owner);
    const ownerIndividualValidation = useValidateIndividual(personType.owner);
    const checkOwnerLegalValidation =
        values.ownerType === legalPersonType.legal && values.isSubmitterOwnerOfVehicle === 'false'
            ? ownerLegalValidation
            : null;
    const checkOwnerIndividualValidation =
        values.ownerType === legalPersonType.individual && values.isSubmitterOwnerOfVehicle === 'false'
            ? ownerIndividualValidation
            : null;

    // owner building validation
    const ownerBuildingLegalValidation = useValidateLegalEntity(personType.ownerBuilding);
    const ownerBuildingIndividualValidation = useValidateIndividual(personType.ownerBuilding);
    const checkOwnerBuildingLegalValidation =
        values.ownerBuildingType === legalPersonType.legal && values.objectOwnerSubmitter === 'false'
            ? ownerBuildingLegalValidation
            : null;
    const checkOwnerBuildingIndividualValidation =
        values.ownerBuildingType === legalPersonType.individual && values.objectOwnerSubmitter === 'false'
            ? ownerBuildingIndividualValidation
            : null;

    // user validation
    const userLegalValidation = useValidateLegalEntity(personType.user);
    const userIndividualValidation = useValidateIndividual(personType.user);
    const checkUserLegalValidation = values.userType === legalPersonType.legal ? userLegalValidation : null;
    const checkUserIndividualValidation =
        values.userType === legalPersonType.individual ? userIndividualValidation : null;

    // driver validation
    const driverValidation = useValidateIndividual(personType.driver);
    const checkDriverlValidation = values.pickDriver === 4 ? driverValidation : null;

    const [currentStep, { goForward, goBack }] = navigator;

    const isVehicle: string = damageType.vehicle;
    const isOther: string = damageType.other;

    values.mobileLabelStar = false;

    const handleNext = (): void => {
        let valid = true;
        setTouched(
            {
                damageType: true,
                ownerType: true,
                userType: true,
                ownerBuildingType: true,
                vehicleType: true,
                chassisNumber: true,
                vehicleBrand: true,
                vehicleModel: true,
                vehicleReg: true,
                vehicleProductionYear: true,
                objectOwnerSubmitter: true,
                objectType: true,
                objectDescription: true,
                objectConstructionYear: true,
                objectLocation: true,
                objectDamageDescription: true,
                ...checkOwnerBuildingIndividualValidation?.touched,
                ...checkOwnerBuildingLegalValidation?.touched,
                vehicleDamageMobile: true,
                isSubmitterOwnerOfVehicle: true,
                submitterUserByVehicleRegistration: true,
                isVehicleDrivable: true,
                vehicleLocation: true,
                howIsVehicleUsed: true,
                pickDriver: true,
                isVehicleParked: true,
                sendLink: true,
                smsLink: true,
                vehicleDamageDescription: true,
                ...checkOwnerLegalValidation?.touched,
                ...checkOwnerIndividualValidation?.touched,
                ...checkUserLegalValidation?.touched,
                ...checkUserIndividualValidation?.touched,
                ...checkDriverlValidation?.touched,
            },
            false,
        );

        let existingErrors: GenericObject = {};

        if (!values.damageType) {
            existingErrors.damageType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (values.damageType === isVehicle) {
            if (!values.vehicleType) {
                existingErrors.vehicleType = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.chassisNumber) {
                existingErrors.chassisNumber = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            } else {
                if ([1, 2, 3, 4, 6, 7].includes(parseInt(values.vehicleType, 10)) && !checkVIN(values.chassisNumber)) {
                    existingErrors.chassisNumber = translations.validationMessages.CHASSIS_NUMBER_INVALID;
                    valid = false;
                }
            }

            if (!values.immaterialOrBothDamage) {
                if (!values.vehicleBrand) {
                    existingErrors.vehicleBrand = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (!values.vehicleModel) {
                    existingErrors.vehicleModel = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (!values.vehicleReg) {
                    existingErrors.vehicleReg = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (!checkVehicleRegistration(values.vehicleReg)) {
                    existingErrors.vehicleReg = translations.validationMessages.REGISTRATION_NUMBER_INVALID_FORMAT;
                    valid = false;
                }

                if (!values.vehicleProductionYear) {
                    existingErrors.vehicleProductionYear = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (
                    parseInt(values.vehicleProductionYear, 10) > currentYear ||
                    parseInt(values.vehicleProductionYear, 10) < 1800
                ) {
                    existingErrors.vehicleProductionYear = translations.validationMessages.INVALID_YEAR;
                    valid = false;
                }

                const relationLegalRepresentative =
                    values.ownerIndividualSubmitterRelationId === 3 || values.ownerLegalEntityPersonRoleId === 3;
                const relationOther =
                    values.ownerIndividualSubmitterRelationId === 12 || values.ownerLegalEntityPersonRoleId === 12;

                if (!values.howIsVehicleUsed && (relationOther || relationLegalRepresentative)) {
                    existingErrors.howIsVehicleUsed = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (!values.submitterUserByVehicleRegistration && relationOther && values.howIsVehicleUsed === 'true') {
                    existingErrors.submitterUserByVehicleRegistration = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (!values.isSubmitterOwnerOfVehicle) {
                    existingErrors.isSubmitterOwnerOfVehicle = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (
                    (!values.ownerType && values.isSubmitterOwnerOfVehicle === 'false') ||
                    (values.isSubmitterOwnerOfVehicle === 'false' && values.ownerType === legalPersonType.initialValue)
                ) {
                    existingErrors.ownerType = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (
                    !values.userType &&
                    values.howIsVehicleUsed === 'true' &&
                    values.submitterUserByVehicleRegistration === 'false'
                ) {
                    existingErrors.userType = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }

                if (!values.isVehicleDrivable) {
                    existingErrors.isVehicleDrivable = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                } else {
                    if (
                        values.isVehicleDrivable === 'false' &&
                        (!values.vehicleLocation || values.vehicleLocation === '')
                    ) {
                        existingErrors.vehicleLocation = translations.validationMessages.REQUIRED_FIELD;
                        valid = false;
                    }
                }

                if (!values.driver && !values.isVehicleParked) {
                    existingErrors.isVehicleParked = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (values.isVehicleParked === 'false' && !values.pickDriver) {
                    existingErrors.pickDriver = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (!values.smsLink) {
                    existingErrors.smsLink = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (values.smsLink === 'true' && !values.sendLink) {
                    existingErrors.sendLink = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (values.smsLink === 'true' && !values.vehicleDamageMobile && values.sendLink === 6) {
                    existingErrors.vehicleDamageMobile = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (
                    values.vehicleDamageMobile &&
                    values.smsLink === 'true' &&
                    values.sendLink === 6 &&
                    (!checkPhoneNumber(values.vehicleDamageMobile) ||
                        !checkIfSerbianPhoneNumber(values.vehicleDamageMobile))
                ) {
                    existingErrors.vehicleDamageMobile = translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
                    valid = false;
                }
                if (!values.vehicleDamageDescription) {
                    existingErrors.vehicleDamageDescription = translations.validationMessages.REQUIRED_FIELD;
                    valid = false;
                }
                if (values.ownerType === legalPersonType.legal) {
                    existingErrors = { ...existingErrors, ...checkOwnerLegalValidation?.errors };
                    if (!checkOwnerLegalValidation?.isValid) valid = false;
                }
                if (values.ownerType === legalPersonType.individual) {
                    existingErrors = { ...existingErrors, ...checkOwnerIndividualValidation?.errors };
                    if (!checkOwnerIndividualValidation?.isValid) valid = false;
                }
                if (values.userType === legalPersonType.legal) {
                    existingErrors = { ...existingErrors, ...checkUserLegalValidation?.errors };
                    if (!checkUserLegalValidation?.isValid) valid = false;
                }
                if (values.userType === legalPersonType.individual) {
                    existingErrors = { ...existingErrors, ...checkUserIndividualValidation?.errors };
                    if (!checkUserIndividualValidation?.isValid) valid = false;
                }
                if (values.isVehicleParked === 'false' && values.pickDriver === 4) {
                    existingErrors = { ...existingErrors, ...checkDriverlValidation?.errors };
                    if (!checkDriverlValidation?.isValid) valid = false;
                }
            }
        }

        if (values.damageType === isOther) {
            if (
                values.objectOwnerSubmitter === 'false' &&
                (!values.ownerBuildingType || values.ownerBuildingType === legalPersonType.initialValue)
            ) {
                existingErrors.ownerBuildingType = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.objectType) {
                existingErrors.objectType = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.objectDescription) {
                existingErrors.objectDescription = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.objectConstructionYear) {
                existingErrors.objectConstructionYear = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (
                parseInt(values.objectConstructionYear, 10) > currentYear ||
                parseInt(values.objectConstructionYear, 10) < 1800
            ) {
                existingErrors.objectConstructionYear = translations.validationMessages.INVALID_YEAR;
                valid = false;
            }

            if (!values.objectLocation) {
                existingErrors.objectLocation = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.objectDamageDescription) {
                existingErrors.objectDamageDescription = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.objectOwnerSubmitter) {
                existingErrors.objectOwnerSubmitter = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (values.ownerBuildingType === legalPersonType.individual) {
                existingErrors = { ...existingErrors, ...checkOwnerBuildingIndividualValidation?.errors };
                if (!checkOwnerBuildingIndividualValidation?.isValid) valid = false;
            }

            if (values.ownerBuildingType === legalPersonType.legal) {
                existingErrors = { ...existingErrors, ...checkOwnerBuildingLegalValidation?.errors };
                if (!checkOwnerBuildingLegalValidation?.isValid) valid = false;
            }
        }

        setErrors(existingErrors);

        if (valid) {
            if (values.requestType === requestType.MaterialDamage) {
                submitForm();
            } else {
                goForward();
            }
        }
    };

    const handleGoBack = (): void => {
        if (values.requestType === requestType.MaterialDamage && values.submitError !== '') {
            setFieldValue('submitError', '');
        }
        goBack();
    };

    const handleChangeDamageType = () => {
        if (values.damageType === damageType.other) {
            setFieldValue('chassisNumber', '');
            setFieldValue('vehicleBrand', '');
            setFieldValue('vehicleModel', '');
            setFieldValue('vehicleReg', '');
            setFieldValue('vehicleProductionYear', '');
            setFieldValue('vehicleType', '');
            setFieldValue('isSubmitterOwnerOfVehicle', '');
            setFieldValue('isVehicleParked', '');
            setFieldValue('isVehicleDrivable', '');
            setFieldValue('vehicleLocation', '');
            setFieldValue('howIsVehicleUsed', '');
            setFieldValue('vehicleDamageDescription', '');
            setFieldValue('vehicleOtherDamages', '');
            setFieldValue('vehicleService', '');
            setFieldValue('smsLink', '');
            setFieldValue('payment', '');
        } else {
            if (values.damageType === damageType.vehicle) {
                setFieldValue('objectDescription', '');
                setFieldValue('objectConstructionYear', '');
                setFieldValue('objectLocation', '');
                setFieldValue('objectDamageDescription', '');
                setFieldValue('moreObjectDamagesDescription', '');
                setFieldValue('objectType', '');
                setFieldValue('objectOwnerSubmitter', '');
            }
        }
    };

    useEffect(() => {
        handleChangeDamageType();
    }, [values.damageType]);

    useEffect(() => {
        if (values.requestType === requestType.MaterialDamage && values.submittedSuccessfully) {
            goForward();
        }
    }, [values.submittedSuccessfully]);

    const RadioGroup = (params: RadioGroupProps) => {
        return (
            <MUIRadioGroup {...params} row>
                <FormControlLabel value={damageType.vehicle} control={<Radio />} label={damageType.vehicle} />
                <Tooltip title={<p>{translations.tooltips.DAMAGE_TYPE_OTHER}</p>} placement="top-start">
                    <FormControlLabel value={damageType.other} control={<Radio />} label={damageType.other} />
                </Tooltip>
            </MUIRadioGroup>
        );
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="subtitle1">
                    {translations.CHOOSE_DAMAGE_TYPE}
                </Typography>

                <FormControl>
                    <Field name="damageType" as={RadioGroup} size="big" />

                    {touched.damageType && errors.damageType ? (
                        <FormHelperText error={true}>
                            <>{errors.damageType}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>

                {values.damageType === isVehicle && <DamageVehicle />}
                {values.damageType === isOther && <DamageObject />}
            </StyledCardSection>
            {values.requestType === requestType.MaterialDamage &&
            !values.submittedSuccessfully &&
            values.submitError !== '' ? (
                <FormHelperText error={true}>
                    <>{values.submitError}</>
                </FormHelperText>
            ) : (
                <></>
            )}
            {values.requestType === requestType.MaterialDamage && isSubmitting ? (
                <CircularProgress />
            ) : (
                <StepperControls
                    onPrevious={handleGoBack}
                    onNext={
                        !values.materialDamage &&
                        !(
                            values.chassisNumber === values.chassisNumberByPolicy &&
                            values.requestType !== requestType.ImmaterialDamage
                        )
                            ? handleNext
                            : undefined
                    }
                    onNextLabel={common.NEXT}
                    disableNext={
                        values.chassisNumber === values.chassisNumberByPolicy &&
                        values.requestType !== requestType.ImmaterialDamage
                    }
                />
            )}
        </StepWrapper>
    );
};

export default DamageScreen;
